"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Input } from "../../ui/input";
import { ArrowLeft, Loader2, Search, Play, MessageSquare } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { 
  getProcedureFileUsageForWorkspace, 
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";

interface InventoryExecutionIPETestingProps {
  onBack?: () => void;
}

interface ExcelColumn {
  name: string;
  value: string;
}

const INPUT_REGISTER_FIELDS: Array<{ key: string; label: string }> = [
  { key: "material_name", label: "Material Name" },
  { key: "material_code", label: "Material Code" },
  { key: "material_type", label: "Material Type" },
  { key: "quantity", label: "Quantity" },
  { key: "value", label: "Value" },
  { key: "cost_per_unit", label: "Cost Per Unit" },
  { key: "total_rm_cost", label: "Total RM Cost" },
  { key: "total_oh_cost", label: "Total OH Cost" },
  { key: "grn_date", label: "GRN Date" },
  { key: "mfg_date", label: "MFG Date" },
];

const SUBSEQUENT_SALES_FIELDS: Array<{ key: string; label: string }> = [
  { key: "material_code", label: "Material Code" },
  { key: "material_name", label: "Material Name" },
  { key: "quantity", label: "Quantity" },
  { key: "value", label: "Value" },
];

export default function InventoryExecutionIPETesting({ onBack }: InventoryExecutionIPETestingProps) {
  // SharePoint search and file listing
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);

  // Separate selections for two inputs
  const [inputRegisterFile, setInputRegisterFile] = useState<string>("");
  const [salesRegisterFile, setSalesRegisterFile] = useState<string>("");

  // Columns per file
  const [inputRegisterColumns, setInputRegisterColumns] = useState<ExcelColumn[]>([]);
  const [salesRegisterColumns, setSalesRegisterColumns] = useState<ExcelColumn[]>([]);
  const [isLoadingInputColumns, setIsLoadingInputColumns] = useState(false);
  const [isLoadingSalesColumns, setIsLoadingSalesColumns] = useState(false);

  // Mappings per field set
  const [inputRegisterMapping, setInputRegisterMapping] = useState<Record<string, string>>({});
  const [salesRegisterMapping, setSalesRegisterMapping] = useState<Record<string, string>>({});

  // Run state
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `INVENTORY_IPE_TESTING_${normalizedSection}`;
  };

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Inventory", "IPE Testing");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  // Auto-trigger search on mount
  useEffect(() => {
    searchForFiles();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_INV");
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  const loadColumnsForInputRegister = async () => {
    if (!inputRegisterFile) return;
    setIsLoadingInputColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(inputRegisterFile);
        if (result.ok && result.columns) {
          const cols = result.columns.map((c: string) => ({ name: c, value: c }));
          setInputRegisterColumns(cols);
          // Save file name to file usage reference
          const fileName = inputRegisterFile.includes(' (') ? inputRegisterFile.split(' (')[0] : inputRegisterFile;
          await updateProcedureFileUsageForWorkspace("Inventory", "IPE Testing", [{ 
            "Search for files": fileName 
          }]);
          await loadFileUsageReference();
        }
      } else {
        const test = [
          "material_name","material_code","material_type","quantity","value",
          "cost_per_unit","total_rm_cost","total_oh_cost","grn_date","mfg_date",
        ];
        setInputRegisterColumns(test.map((c) => ({ name: c, value: c })));
      }
    } finally {
      setIsLoadingInputColumns(false);
    }
  };

  const loadColumnsForSalesRegister = async () => {
    if (!salesRegisterFile) return;
    setIsLoadingSalesColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(salesRegisterFile);
        if (result.ok && result.columns) {
          const cols = result.columns.map((c: string) => ({ name: c, value: c }));
          setSalesRegisterColumns(cols);
          // Save file name to file usage reference
          const fileName = salesRegisterFile.includes(' (') ? salesRegisterFile.split(' (')[0] : salesRegisterFile;
          await updateProcedureFileUsageForWorkspace("Inventory", "IPE Testing", [{ 
            "Subsequent Sales Register": fileName 
          }]);
          await loadFileUsageReference();
        }
      } else {
        const test = ["material_code","material_name","quantity","value"];
        setSalesRegisterColumns(test.map((c) => ({ name: c, value: c })));
      }
    } finally {
      setIsLoadingSalesColumns(false);
    }
  };

  const handleInputRegisterMapping = (key: string, columnName: string) => {
    setInputRegisterMapping((prev) => ({ ...prev, [key]: columnName }));
  };

  const handleSalesRegisterMapping = (key: string, columnName: string) => {
    setSalesRegisterMapping((prev) => ({ ...prev, [key]: columnName }));
  };

  const allIRMapped = INPUT_REGISTER_FIELDS.every(({ key }) => !!inputRegisterMapping[key]);
  const allSSRMapped = SUBSEQUENT_SALES_FIELDS.every(({ key }) => !!salesRegisterMapping[key]);

  const runInventoryIPE = async () => {
    if (!allIRMapped || !allSSRMapped) return;
    setProcessingStatus("running");
    try {
      // Save column mappings to file usage reference
      const entries: FileUsageEntry[] = [];
      const inputFileName = inputRegisterFile.includes(' (') ? inputRegisterFile.split(' (')[0] : inputRegisterFile;
      const salesFileName = salesRegisterFile.includes(' (') ? salesRegisterFile.split(' (')[0] : salesRegisterFile;
      entries.push({ "Search for files": inputFileName });
      entries.push({ "Subsequent Sales Register": salesFileName });
      
      const inventoryMapping: Record<string, string> = {};
      INPUT_REGISTER_FIELDS.forEach(field => {
        if (inputRegisterMapping[field.key]) {
          inventoryMapping[field.label] = inputRegisterMapping[field.key];
        }
      });
      const salesMapping: Record<string, string> = {};
      SUBSEQUENT_SALES_FIELDS.forEach(field => {
        if (salesRegisterMapping[field.key]) {
          salesMapping[field.label] = salesRegisterMapping[field.key];
        }
      });
      if (Object.keys(inventoryMapping).length > 0 || Object.keys(salesMapping).length > 0) {
        entries.push({ 
          "Columns of Inventory Register and Subsequent Sales Register": JSON.stringify({
            inventory: inventoryMapping,
            sales: salesMapping
          })
        });
      }
      if (entries.length > 0) {
        await updateProcedureFileUsageForWorkspace("Inventory", "IPE Testing", entries);
        await loadFileUsageReference();
      }

      const ir_custom_keys = INPUT_REGISTER_FIELDS.map(({ key }) => inputRegisterMapping[key]);
      const ssr_custom_keys = SUBSEQUENT_SALES_FIELDS.map(({ key }) => salesRegisterMapping[key]);

      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_inv_ipe_testing", {
          inputFiles: [],
          options: {
            ir_custom_keys,
            ssr_custom_keys,
          },
        });

        if (result.ok) {
          setProcessingStatus("completed");
        } else {
          setProcessingStatus("error");
        }
      } else {
        setProcessingStatus("error");
      }
    } catch {
      setProcessingStatus("error");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Execution - Inventory | IPE Testing</h2>
          <p className="text-gray-400">Step 1: Search files, then load columns and map fields</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-8">
              <div className="space-y-4">
                <Label className="text-white">Inventory Register</Label>
                <div className="space-y-3">
                  <Select value={inputRegisterFile} onValueChange={setInputRegisterFile}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select file" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {foundFiles.map((f) => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Search for files"]) && (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {fileUsageReference.find(e => e["Search for files"])?.["Search for files"]}
                    </p>
                  )}
                  {inputRegisterFile && (
                    <div className="flex items-center gap-3">
                      <Button onClick={loadColumnsForInputRegister} disabled={!inputRegisterFile || isLoadingInputColumns} className="bg-blue-600 hover:bg-blue-700 text-white">
                        {isLoadingInputColumns ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...</>
                        ) : (
                          "Load Columns"
                        )}
                      </Button>
                      {inputRegisterColumns.length > 0 && (
                        <span className="text-green-400 text-sm"> {inputRegisterColumns.length} columns loaded</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {INPUT_REGISTER_FIELDS.map(({ key, label }) => (
                    <div key={key} className="space-y-2">
                      <Label className="text-white">{label}</Label>
                      <Select
                        value={inputRegisterMapping[key] || ""}
                        onValueChange={(v) => handleInputRegisterMapping(key, v)}
                        disabled={inputRegisterColumns.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={inputRegisterColumns.length === 0 ? "Load columns first" : "Select column"} />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-black/90 text-white">
                          {inputRegisterColumns.map((col) => (
                            <SelectItem key={col.value} value={col.value}>{col.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <Label className="text-white">Subsequent Sales Register</Label>
                <div className="space-y-3">
                  <Select value={salesRegisterFile} onValueChange={setSalesRegisterFile}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select file" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {foundFiles.map((f) => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Subsequent Sales Register"]) && (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {fileUsageReference.find(e => e["Subsequent Sales Register"])?.["Subsequent Sales Register"]}
                    </p>
                  )}
                  {salesRegisterFile && (
                    <div className="flex items-center gap-3">
                      <Button onClick={loadColumnsForSalesRegister} disabled={!salesRegisterFile || isLoadingSalesColumns} className="bg-blue-600 hover:bg-blue-700 text-white">
                        {isLoadingSalesColumns ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...</>
                        ) : (
                          "Load Columns"
                        )}
                      </Button>
                      {salesRegisterColumns.length > 0 && (
                        <span className="text-green-400 text-sm"> {salesRegisterColumns.length} columns loaded</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {SUBSEQUENT_SALES_FIELDS.map(({ key, label }) => (
                    <div key={key} className="space-y-2">
                      <Label className="text-white">{label}</Label>
                      <Select
                        value={salesRegisterMapping[key] || ""}
                        onValueChange={(v) => handleSalesRegisterMapping(key, v)}
                        disabled={salesRegisterColumns.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={salesRegisterColumns.length === 0 ? "Load columns first" : "Select column"} />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-black/90 text-white">
                          {salesRegisterColumns.map((col) => (
                            <SelectItem key={col.value} value={col.value}>{col.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fileUsageReference.find(e => e["Columns of Inventory Register and Subsequent Sales Register"]) && (() => {
                        try {
                          const mapping = JSON.parse(fileUsageReference.find(e => e["Columns of Inventory Register and Subsequent Sales Register"])?.["Columns of Inventory Register and Subsequent Sales Register"] || "{}");
                          const lastUsed = mapping.sales?.[label];
                          return lastUsed ? (
                            <p className="text-xs text-white/60 mt-1">
                              Last used: {lastUsed}
                            </p>
                          ) : null;
                        } catch {
                          return null;
                        }
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5 text-green-500" />
              Run IPE Testing
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Run IPE Testing"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={runInventoryIPE}
              disabled={!allIRMapped || !allSSRMapped || processingStatus === "running"}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {processingStatus === "running" ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                "Run IPE Testing"
              )}
            </Button>
            {processingStatus === "completed" && (
              <span className="text-green-400">Successful</span>
            )}
            {processingStatus === "error" && (
              <span className="text-red-400">Failed</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Run IPE Testing")]: "Run IPE Testing",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}


